import { useTranslation, type UseTranslationResponse } from 'react-i18next';

/**
 * useTranslation that survives being called before i18n has initialised.
 *
 * Two things this file used to get wrong.
 *
 * 1. It returned a union of the real useTranslation result and a fallback whose
 *    `t` was typed `(key: string, fallback?: string) => string`. TypeScript
 *    resolves a call on a union to the intersection of its signatures, so every
 *    `t('key', { count })` in the app failed to typecheck — 75 errors, all from
 *    here, which is most of what made `tsc` unusable as a gate.
 *
 * 2. The fallback path called useCallback. Hooks must run in the same order on
 *    every render, and this one only ran while i18n was uninitialised — so the
 *    render where initialisation completed changed the hook count and React
 *    would throw "rendered fewer hooks than expected".
 *
 * The fallback is now a plain module-level function typed to match i18next's
 * own `t`, so callers see one consistent signature either way.
 */

type SafeTranslation = UseTranslationResponse<any, any>;

/**
 * Stand-in for `t` before i18n is ready. Honours an explicit defaultValue or a
 * bare string fallback; otherwise echoes the key, which is what a missing
 * translation should look like. It cannot interpolate — that is the point of
 * it being a fallback, not a translator.
 */
const fallbackT = ((key: string, options?: unknown): string => {
  if (typeof options === 'string') return options;

  if (options && typeof options === 'object' && 'defaultValue' in options) {
    const withDefault = options as { defaultValue?: unknown };
    if (withDefault.defaultValue != null) return String(withDefault.defaultValue);
  }

  return key;
}) as SafeTranslation['t'];

const FALLBACK = {
  t: fallbackT,
  i18n: {
    language: 'ru',
    languages: ['ru', 'en'],
    isInitialized: false,
    changeLanguage: async () => undefined,
  },
  ready: false,
} as unknown as SafeTranslation;

export const useSafeTranslation = (namespace?: string): SafeTranslation => {
  try {
    const translation = useTranslation(namespace);
    return translation.i18n?.isInitialized ? translation : FALLBACK;
  } catch (error) {
    console.warn('Translation context not available, using fallback:', error);
    return FALLBACK;
  }
};

export default useSafeTranslation;
